import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, Eye, EyeOff, Building2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import CheckELogo from "@/components/CheckELogo";
import LegalAcceptanceCheckbox, { useLegalAcceptance } from "@/components/LegalAcceptanceCheckbox";
import { LegalAcceptanceError, describeAcceptanceFailure } from "@/lib/legalAcceptance";

/**
 * RegisterPage — Clean Enterprise restyle.
 * Same chrome as the landing page. Left pane = signup form. Right pane =
 * quiet brand context + a "skip signup, just submit AI output" hook.
 */
export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", password: "", company: "",
  });

  const passwordRequirements = useMemo(() => ({
    minLength:    formData.password.length >= 8,
    hasUppercase: /[A-Z]/.test(formData.password),
    hasLowercase: /[a-z]/.test(formData.password),
    hasNumber:    /\d/.test(formData.password),
  }), [formData.password]);

  const isPasswordValid = useMemo(
    () => Object.values(passwordRequirements).every(Boolean),
    [passwordRequirements],
  );

  const [acceptedLegal, setAcceptedLegal] = useState(false);
  // The account exists as soon as register() succeeds, but the sign-up is not
  // finished until the Terms/Privacy acceptance is on file. If recording fails
  // we keep the user here (account created, retry available) instead of
  // letting them through with no proof of acceptance.
  const [accountCreated, setAccountCreated] = useState(false);
  const [acceptanceError, setAcceptanceError] = useState(null);
  const { recordAll: recordAcceptances } = useLegalAcceptance(["terms_of_service", "privacy_policy"]);

  const isFormValid = useMemo(
    () => formData.name.trim() && formData.email.trim() && isPasswordValid && acceptedLegal,
    [formData.name, formData.email, isPasswordValid, acceptedLegal],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }
    if (!isPasswordValid) {
      toast.error("Please meet all password requirements");
      return;
    }
    if (!acceptedLegal) {
      toast.error("Please accept the Terms of Service and Privacy Policy");
      return;
    }
    setLoading(true);
    setAcceptanceError(null);
    try {
      if (!accountCreated) {
        await register(
          formData.email.toLowerCase().trim(),
          formData.password,
          formData.name.trim(),
          formData.company?.trim(),
        );
        setAccountCreated(true);
      }
      // Token is in localStorage ("xpert_token") after register() succeeds.
      // Throws LegalAcceptanceError if the acceptance could not be recorded.
      await recordAcceptances();
      toast.success("Account created.");
      navigate("/");
    } catch (error) {
      if (error instanceof LegalAcceptanceError) {
        setAcceptanceError(describeAcceptanceFailure(error));
        return;
      }
      // Surface the real reason from the backend if we have it.
      // Falls back to axios's network-level message, then a generic.
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to create account";
      const friendly =
        typeof detail === "string" && detail.toLowerCase().includes("already")
          ? "That email is already registered. Try signing in instead."
          : typeof detail === "string"
            ? detail
            : "Failed to create account";
      toast.error(friendly);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas text-ink font-body flex flex-col">
      <MarketingNav />

      <main className="flex-1 grid lg:grid-cols-2">
        {/* Left — quiet brand pane */}
        <div className="hidden lg:flex items-center justify-center bg-ink text-white relative overflow-hidden order-1">
          <div aria-hidden="true"
               className="absolute -bottom-32 -left-24 w-[500px] h-[500px] rounded-full bg-brand-green/30 blur-3xl" />
          <div className="max-w-md p-12 relative">
            <CheckELogo size={48} />
            <h2 className="mt-8 font-display text-3xl leading-snug">
              Join the human review layer for AI-built work.
            </h2>
            <p className="mt-5 text-white/70 text-lg leading-relaxed">
              Send AI-built work for a written review. Today, Ankit Kumar,
              Xpert's founder, writes every verdict.
            </p>
            <div className="mt-12 pt-6 border-t border-white/10 text-sm text-white/70">
              Want to share your expertise?{" "}
              <Link to="/become-expert" className="text-brand-green font-semibold hover:underline">
                Become an expert →
              </Link>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div className="flex items-center justify-center px-4 sm:px-6 py-12 sm:py-16 order-2">
          <div className="w-full max-w-md">
            {/* CLAUDE.md Rule §5 — canonical eyebrow line. */}
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green mb-4">
              The human review layer for AI-built work
            </p>
            <h1 className="font-display text-4xl sm:text-5xl text-ink leading-tight">
              Create your account.
            </h1>
            <p className="text-slateline-600 mt-3">
              30-second signup. No credit card needed to start.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <FieldLabel htmlFor="name">Full name</FieldLabel>
              <FieldInputWithIcon icon={User}>
                <input
                  id="name" type="text" placeholder="Ada Lovelace" disabled={accountCreated}
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  className={fieldInputClass}
                />
              </FieldInputWithIcon>

              <FieldLabel htmlFor="email">Email</FieldLabel>
              <FieldInputWithIcon icon={Mail}>
                <input
                  id="email" type="email" placeholder="you@company.com" disabled={accountCreated}
                  value={formData.email}
                  onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                  className={fieldInputClass}
                />
              </FieldInputWithIcon>

              <FieldLabel htmlFor="company">
                Company <span className="text-slateline-400 normal-case font-normal">(optional)</span>
              </FieldLabel>
              <FieldInputWithIcon icon={Building2}>
                <input
                  id="company" type="text" placeholder="Your company" disabled={accountCreated}
                  value={formData.company}
                  onChange={(e) => setFormData((p) => ({ ...p, company: e.target.value }))}
                  className={fieldInputClass}
                />
              </FieldInputWithIcon>

              <FieldLabel htmlFor="password">Password</FieldLabel>
              <FieldInputWithIcon icon={Lock}>
                <input
                  id="password" type={showPassword ? "text" : "password"}
                  placeholder="Create a strong password" disabled={accountCreated}
                  value={formData.password}
                  onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                  className={`${fieldInputClass} pr-10`}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slateline-400 hover:text-ink">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </FieldInputWithIcon>

              {/* Password requirement chips */}
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                <PwReq ok={passwordRequirements.minLength}    label="8+ characters" />
                <PwReq ok={passwordRequirements.hasUppercase} label="1 uppercase" />
                <PwReq ok={passwordRequirements.hasLowercase} label="1 lowercase" />
                <PwReq ok={passwordRequirements.hasNumber}    label="1 number" />
              </div>

              <LegalAcceptanceCheckbox
                policies={[
                  { id: "terms_of_service", label: "Terms of Service", href: "/terms" },
                  { id: "privacy_policy",   label: "Privacy Policy",   href: "/privacy" },
                ]}
                checked={acceptedLegal}
                onChange={setAcceptedLegal}
                className="pt-2"
              />

              {acceptanceError && (
                <div role="alert"
                     className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  <p className="font-semibold">Your account is created, but we couldn't finish sign-up.</p>
                  <p className="mt-1">{acceptanceError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !isFormValid}
                className="w-full inline-flex items-center justify-center h-11 rounded-md
                           bg-brand-green hover:bg-brand-greenHover disabled:opacity-50 disabled:cursor-not-allowed
                           text-white font-semibold text-sm tracking-wide shadow-sm transition-colors"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : accountCreated ? "Try again" : "Create account"}
              </button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slateline-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-canvas text-slateline-400 uppercase tracking-wider font-semibold">
                  or
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + "/dashboard";
                const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://api.xpert.company";
                window.location.href = `${backendUrl}/api/auth/google?redirect_uri=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full inline-flex items-center justify-center gap-3 h-11 rounded-md
                         border border-slateline-300 bg-white hover:bg-slateline-50
                         text-ink font-medium text-sm transition-colors"
              data-testid="google-signup-btn"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign up with Google
            </button>

            <p className="text-center text-sm text-slateline-500 mt-6">
              Already have an account?{" "}
              <Link to="/login" className="text-brand-green hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form bits — kept inline to avoid scattering primitives
// ---------------------------------------------------------------------------

const fieldInputClass =
  "w-full pl-9 pr-3 h-11 bg-white border border-slateline-200 rounded-md " +
  "text-ink placeholder-slateline-400 outline-none " +
  "focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors";

function FieldLabel({ htmlFor, children }) {
  return (
    <label htmlFor={htmlFor}
           className="text-xs font-semibold uppercase tracking-wider text-slateline-500">
      {children}
    </label>
  );
}

function FieldInputWithIcon({ icon: Icon, children }) {
  return (
    <div className="mt-1.5 relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slateline-400" />
      {children}
    </div>
  );
}

function PwReq({ ok, label }) {
  return (
    <div className={`flex items-center gap-1.5 ${ok ? "text-brand-green" : "text-slateline-400"}`}>
      {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
      <span>{label}</span>
    </div>
  );
}
